/**
 * TS-Spiegel von frontend/shared/design-preview.js – bei Änderungen beide Dateien sync halten.
 */

export const NEW_DESIGN_STORAGE_KEY = "eh:new-design";
export const NEW_DESIGN_CHANGE_EVENT = "eh:new-design-change";
export const NEW_DESIGN_PENDING_KEY = "eh:new-design-pending";
export const LEGACY_URL_PREFIX = "/alt";

let cachedNewDesign: boolean | null = null;

export const REACT_NAV_KEYS = new Set([
  "orders",
  "bestellwesen",
  "bestellungen",
  "anfragen",
  "materialanforderungen",
  "admin",
  "personal",
  "zeiterfassung",
  "timesheets",
  "support",
  "wartungen",
  "reports",
]);

const NAV_ROUTES: Record<string, { legacy: string; react: string }> = {
  orders: { legacy: "/alt/auftraege.html", react: "/projekte" },
  bestellwesen: { legacy: "/alt/bestellungen.html", react: "/bestellwesen/bestellungen" },
  bestellungen: { legacy: "/alt/bestellungen.html", react: "/bestellwesen/bestellungen" },
  anfragen: { legacy: "/alt/anfragen.html", react: "/bestellwesen/anfragen" },
  materialanforderungen: { legacy: "/alt/materialanforderungen.html", react: "/bestellwesen/materialanforderungen" },
  admin: { legacy: "/alt/admin/", react: "/admin" },
  personal: { legacy: "/alt/personal/", react: "/personal" },
  zeiterfassung: { legacy: "/alt/zeiterfassung.html", react: "/zeiterfassung" },
  timesheets: { legacy: "/alt/stundennachweise", react: "/stundennachweise" },
  support: { legacy: "/alt/support.html", react: "/support" },
  wartungen: { legacy: "/alt/wartungen/", react: "/wartungen" },
  reports: { legacy: "/alt/auswertungen.html", react: "/auswertungen" },
};

const PAGE_PREFIX_MAP = [
  { legacyPrefix: "/alt/admin/einstellungen", reactPrefix: "/admin/einstellungen" },
  { legacyPrefix: "/alt/admin/admin-area", reactPrefix: "/admin/admin-area" },
  { legacyPrefix: "/alt/admin/field-templates", reactPrefix: "/admin/field-templates" },
  { legacyPrefix: "/alt/admin/mitarbeiter", reactPrefix: "/admin/mitarbeiter" },
  { legacyPrefix: "/alt/admin/zeiterfassung", reactPrefix: "/admin/zeiterfassung" },
  { legacyPrefix: "/alt/admin", reactPrefix: "/admin" },
  {
    legacyPrefix: "/alt/wartungen/brandschutztuerwartung",
    reactPrefix: "/wartungen/brandschutztuerwartung",
  },
  { legacyPrefix: "/alt/offpaper", reactPrefix: "/offpaper" },
  { legacyPrefix: "/alt/wartungen", reactPrefix: "/wartungen" },
  { legacyPrefix: "/alt/personal", reactPrefix: "/personal" },
  { legacyPrefix: "/alt/zeiterfassung", reactPrefix: "/zeiterfassung" },
  { legacyPrefix: "/alt/auswertungen", reactPrefix: "/auswertungen" },
  { legacyPrefix: "/alt/auftraege", reactPrefix: "/projekte" },
  { legacyPrefix: "/alt/bestellungen", reactPrefix: "/bestellwesen/bestellungen" },
  { legacyPrefix: "/alt/anfragen", reactPrefix: "/bestellwesen/anfragen" },
  { legacyPrefix: "/alt/materialanforderungen", reactPrefix: "/bestellwesen/materialanforderungen" },
  { legacyPrefix: "/alt/stundennachweise", reactPrefix: "/stundennachweise" },
  { legacyPrefix: "/alt/support", reactPrefix: "/support" },
  { legacyPrefix: "/alt/login", reactPrefix: "/login" },
  { legacyPrefix: "/alt", reactPrefix: "" },
] as const;

export function normalizeRoutePath(path: string): string {
  if (!path) return "/";
  let p = String(path).split("?")[0]?.split("#")[0] ?? "/";
  if (!p.startsWith("/")) p = `/${p}`;
  if (p !== "/" && p.endsWith("/")) p = p.slice(0, -1);
  if (p.endsWith(".html")) p = p.slice(0, -5);
  return p || "/";
}

export function isLegacyPath(pathname: string): boolean {
  return pathname === LEGACY_URL_PREFIX || pathname.startsWith(`${LEGACY_URL_PREFIX}/`);
}

export function markNewDesignPending(enabled: boolean): void {
  try {
    sessionStorage.setItem(NEW_DESIGN_PENDING_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function clearNewDesignPending(): void {
  try {
    sessionStorage.removeItem(NEW_DESIGN_PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export function readNewDesignPending(): boolean | null {
  try {
    const value = sessionStorage.getItem(NEW_DESIGN_PENDING_KEY);
    if (value === "1") return true;
    if (value === "0") return false;
  } catch {
    /* ignore */
  }
  return null;
}

export function isNewDesignEnabled(): boolean {
  if (cachedNewDesign !== null) return cachedNewDesign;
  try {
    const value = localStorage.getItem(NEW_DESIGN_STORAGE_KEY);
    if (value === "1") return true;
    if (value === "0") return false;
  } catch {
    /* ignore */
  }
  // Ohne gespeicherte Präferenz: React-/Normal-App bevorzugen.
  return true;
}

export function loadNewDesignPreferenceLocal(): boolean {
  return isNewDesignEnabled();
}

export function applyNewDesignLocally(enabled: boolean): void {
  cachedNewDesign = enabled;
  try {
    if (enabled) localStorage.setItem(NEW_DESIGN_STORAGE_KEY, "1");
    else localStorage.setItem(NEW_DESIGN_STORAGE_KEY, "0");
  } catch {
    /* ignore */
  }
  window.dispatchEvent(
    new CustomEvent(NEW_DESIGN_CHANGE_EVENT, { detail: { enabled: !!enabled } }),
  );
}

export function setNewDesignEnabled(enabled: boolean): void {
  applyNewDesignLocally(enabled);
}

export function resolveLegacyNavHref(
  navKey: string,
  legacyHref: string,
  newDesignEnabled = isNewDesignEnabled(),
): string {
  if (!newDesignEnabled) return legacyHref;
  return NAV_ROUTES[navKey]?.react ?? legacyHref;
}

export function resolveReactNavHref(
  navKey: string,
  reactHref: string,
  legacyHref: string,
  newDesignEnabled = isNewDesignEnabled(),
): string {
  if (newDesignEnabled) return reactHref;
  return NAV_ROUTES[navKey]?.legacy ?? legacyHref;
}

export function mapLegacyPathToReact(pathname: string, search = "", hash = ""): string | null {
  const current = normalizeRoutePath(pathname);
  for (const { legacyPrefix, reactPrefix } of PAGE_PREFIX_MAP) {
    const normLegacy = normalizeRoutePath(legacyPrefix);
    if (current === normLegacy || current.startsWith(`${normLegacy}/`)) {
      const suffix = current.slice(normLegacy.length);
      const target = reactPrefix ? `${reactPrefix}${suffix}` : "/";
      return `${target}${search}${hash}`;
    }
  }
  return null;
}

function legacyPathFromMatch(legacyPrefix: string, suffix: string): string {
  const legacyBase = normalizeRoutePath(legacyPrefix);
  if (legacyBase === "/alt") {
    return "/alt/";
  }
  if (legacyBase === "/alt/login") {
    return "/alt/login";
  }
  if (legacyBase === "/alt/admin") {
    return suffix ? `${LEGACY_URL_PREFIX}/admin${suffix}/` : "/alt/admin/";
  }
  if (legacyBase === "/alt/admin/einstellungen") {
    return "/alt/admin/einstellungen.html";
  }
  if (legacyBase === "/alt/stundennachweise") {
    return "/alt/stundennachweise";
  }
  if (legacyBase === "/alt/wartungen") {
    return suffix ? `/alt/wartungen${suffix}.html` : "/alt/wartungen/";
  }
  return suffix ? `${legacyBase}${suffix}.html` : `${legacyBase}.html`;
}

export function resolveDesignToggleRedirectTarget(
  enabled: boolean,
  pathname: string,
  search = "",
  hash = "",
): string | null {
  const current = `${pathname}${search}${hash}`;

  if (enabled) {
    if (!isLegacyPath(pathname)) return null;
    const mapped = mapLegacyPathToReact(pathname, search, hash);
    if (mapped && mapped !== current) return mapped;
    const fallback = `${resolvePostLoginHome(true)}${search}${hash}`;
    return fallback !== current ? fallback : null;
  }

  // Klassische /alt/-UI wird nicht mehr ausgeliefert.
  return null;
}

export function mapReactPathToLegacy(pathname: string, search = "", hash = ""): string | null {
  if (isLegacyPath(pathname)) return null;
  const current = normalizeRoutePath(pathname);

  for (const { legacyPrefix, reactPrefix } of PAGE_PREFIX_MAP) {
    const normReact = normalizeRoutePath(reactPrefix || "/");
    if (current === normReact || current.startsWith(`${normReact}/`)) {
      const suffix = current.slice(normReact.length);
      return `${legacyPathFromMatch(legacyPrefix, suffix)}${search}${hash}`;
    }
  }
  return null;
}

export function hasReactEquivalent(pathname: string): boolean {
  if (isLegacyPath(pathname)) {
    return mapLegacyPathToReact(pathname) !== null;
  }
  return mapReactPathToLegacy(pathname) !== null;
}

export function resolveDesignPreviewPairHref(
  legacyHref: string,
  reactHref: string,
  newDesignEnabled = isNewDesignEnabled(),
): string {
  return newDesignEnabled ? reactHref : legacyHref;
}

export function resolvePostLoginHome(_newDesignEnabled = isNewDesignEnabled()): string {
  return "/";
}

/** OAuth-Ziel abhängig von der Login-Seite – funktioniert auch ohne localStorage. */
export function resolvePostLoginHomeFromLoginPage(): string {
  if (typeof window !== "undefined") {
    const path = normalizeRoutePath(window.location.pathname);
    if (path === "/login") return "/";
    if (path === "/alt/login") return "/alt/";
  }
  return resolvePostLoginHome();
}

export function resolveLoginPath(_newDesignEnabled = isNewDesignEnabled()): string {
  return "/login";
}

export function applyDesignPreviewPageLinks(root: ParentNode = document): void {
  const enabled = isNewDesignEnabled();
  root.querySelectorAll("[data-href-legacy][data-href-react]").forEach((el) => {
    if (!(el instanceof HTMLAnchorElement)) return;
    const legacy = el.dataset.hrefLegacy || "";
    const react = el.dataset.hrefReact || "";
    if (!legacy || !react) return;
    el.href = resolveDesignPreviewPairHref(legacy, react, enabled);
  });
}

export function maybeRedirectLegacyToReactOnLoad(): boolean {
  if (!isNewDesignEnabled() || !isLegacyPath(window.location.pathname)) return false;
  const { pathname, search, hash } = window.location;
  const target = resolveDesignToggleRedirectTarget(true, pathname, search, hash);
  if (target) {
    window.location.replace(target);
    return true;
  }
  return false;
}

export function resolveSoftNavHref(href: string): string {
  if (!isNewDesignEnabled()) return href;
  try {
    const url = new URL(href, window.location.origin);
    return mapLegacyPathToReact(url.pathname, url.search, url.hash) || href;
  } catch {
    return href;
  }
}

export function maybeRedirectReactToLegacyOnLoad(pathname: string, search = "", hash = ""): boolean {
  if (isNewDesignEnabled()) return false;
  const target = resolveDesignToggleRedirectTarget(false, pathname, search, hash);
  if (target) {
    window.location.replace(target);
    return true;
  }
  return false;
}
